/**
 * Konum satiri icin menu yolu (Yonlendirme Faz 3.2): acik sayfanin menude
 * nerede oldugunu bulur, ornegin "Gorusme & Toplanti › Goruseceğim Firmalar".
 * Menude olmayan sayfalar (detay sayfalari vb.) icin `null` doner; o zaman
 * konum satiri gosterilmez.
 */

export interface MenuTrailItem {
    title: string;
    to?: string;
    classsChange?: string;
    content?: MenuTrailItem[];
}

/** Menudeki adres ("company-list?filter=visitor") → yol + sorgu parametreleri. */
const parseMenuTarget = (to: string) => {
    const [path, query = ''] = to.replace(/^\//, '').split('?');
    return { path, params: new URLSearchParams(query) };
};

const normalizePath = (pathname: string) => pathname.replace(/^\/+|\/+$/g, '');

/**
 * Menude olmayan alt sayfalar → menudeki ust sayfasi. Boylece detay
 * sayfasindayken de sol menude ust sayfa secili gorunur ve konum satiri
 * "… › Toplantilarim › Toplanti Detayi" olur. `titleKey` yoksa sayfa ust
 * sayfanin baska bir adresidir (ek konum adimi eklenmez).
 * Sira onemli: "meetings/list" ve "meetings/create", "meetings/:id"den once gelmeli.
 */
export const MENU_DETAIL_ROUTES: Array<{ pattern: string; parent: string; titleKey?: string }> = [
    // Liste sayfasinin kendisi "meetings/:id" kalibina da uyuyor; once kendisi eslenir.
    { pattern: 'meetings/list', parent: 'meetings/list' },
    { pattern: 'meetings/create', parent: 'meetings/list', titleKey: 'pageLocation.detail.meetingCreate' },
    { pattern: 'meetings/:id', parent: 'meetings/list', titleKey: 'pageLocation.detail.meeting' },
    { pattern: 'company-detail/:id', parent: 'company-list', titleKey: 'pageLocation.detail.company' },
    { pattern: 'company-comments/:id', parent: 'company-list', titleKey: 'pageLocation.detail.companyComments' },
    { pattern: 'users/:id', parent: 'visitors', titleKey: 'pageLocation.detail.user' },
    { pattern: 'user/:id', parent: 'visitors', titleKey: 'pageLocation.detail.user' },
    { pattern: 'event/:id', parent: 'event', titleKey: 'pageLocation.detail.event' },
    { pattern: 'event-sessions/:id', parent: 'event', titleKey: 'pageLocation.detail.eventSessions' },
    { pattern: 'session-detail/:eventId/:sessionId', parent: 'event', titleKey: 'pageLocation.detail.session' },
    { pattern: 'tables/:id', parent: 'tables', titleKey: 'pageLocation.detail.table' },
    { pattern: 'masa-randevularim', parent: 'tables', titleKey: 'pageLocation.detail.tableBookings' },
    { pattern: 'products-create', parent: 'products', titleKey: 'pageLocation.detail.productCreate' },
    { pattern: 'catalogs-create', parent: 'catalogs', titleKey: 'pageLocation.detail.catalogCreate' },
    { pattern: 'b2b-announcements', parent: 'announcements' },
    { pattern: 'b2b/announcements', parent: 'announcements' },
    { pattern: 'b2b-admin-tables/:id/bookings', parent: 'b2b-admin-tables', titleKey: 'pageLocation.detail.adminTableBookings' },
    { pattern: 'b2b-admin-tables/:id/claims', parent: 'b2b-admin-tables', titleKey: 'pageLocation.detail.adminTableClaims' },
];

const patternMatches = (pattern: string, path: string) => {
    const expected = pattern.split('/');
    const actual = path.split('/');
    return (
        expected.length === actual.length &&
        expected.every((segment, index) => (segment.startsWith(':') ? actual[index] !== '' : segment === actual[index]))
    );
};

/** Adresin menudeki karsiligi: kendisi menudeyse kendisi, alt sayfaysa ust sayfasi. */
export const resolveMenuPath = (pathname: string): { menuPath: string; detailTitleKey?: string } => {
    const path = normalizePath(pathname);
    const detail = MENU_DETAIL_ROUTES.find((route) => patternMatches(route.pattern, path));
    return detail ? { menuPath: detail.parent, detailTitleKey: detail.titleKey } : { menuPath: path };
};

/** Menu ogesinin sorgu parametrelerinin hepsi adreste de var mi? */
const queryMatches = (expected: URLSearchParams, actual: URLSearchParams) =>
    Array.from(expected.entries()).every(([key, value]) => actual.get(key) === value);

/**
 * Acik sayfanin menu yolu: grup basligi (varsa) + sayfanin menu adi; ceviri
 * anahtarlari olarak doner. Ayni yola giden birden cok oge varsa sorgusu
 * eslesen secilir; hicbirinin sorgusu eslesmiyorsa yalnizca grup gosterilir
 * (ornegin filtresiz "company-list" → "Firmalar").
 */
export function findMenuTrail(menu: MenuTrailItem[], pathname: string, search = ''): string[] | null {
    const { menuPath: path, detailTitleKey } = resolveMenuPath(pathname);
    if (!path) return null;
    const params = new URLSearchParams(search);
    const withDetail = (trail: string[]) => (detailTitleKey ? [...trail, detailTitleKey] : trail);

    let groupOnlyMatch: string[] | null = null;
    for (const item of menu) {
        if (item.classsChange === 'menu-title') continue;
        const children = Array.isArray(item.content) && item.content.length > 0 ? item.content : [item];
        for (const child of children) {
            if (!child.to) continue;
            const target = parseMenuTarget(child.to);
            if (target.path !== path) continue;
            const trail = child === item ? [item.title] : [item.title, child.title];
            if (queryMatches(target.params, params)) return withDetail(trail);
            if (!groupOnlyMatch) groupOnlyMatch = [item.title];
        }
    }
    return groupOnlyMatch ? withDetail(groupOnlyMatch) : null;
}
